/**
 * Utilities for error-triggered replay with deduplication and filtering
 */

// Error fingerprinting cache (session-scoped)
interface ErrorCacheEntry {
  lastSeen: number;
  count: number;
  lastUserAction: string;
}

const errorCache = new Map<string, ErrorCacheEntry>();

/**
 * Hash stack trace for stable fingerprinting
 */
const hashStack = (stack: string): string => {
  // Use first 3 stack frames (skip first line which is message)
  const frames = stack.split("\n").slice(1, 4);
  const stackKey = frames.join("|");

  // Simple hash (FNV-1a-like)
  let hash = 2166136261;
  for (let i = 0; i < stackKey.length; i++) {
    hash ^= stackKey.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

/**
 * Generate fingerprint for error deduplication
 * Uses error type + stack hash + route for stability
 */
export const getErrorFingerprint = (
  error: Error | string,
  stackOverride?: string
): string => {
  const err = error instanceof Error ? error : new Error(String(error));
  // Normalize route so ID-heavy routes don't fragment deduplication buckets.
  // E.g. /appointment/details/123 and /appointment/details/456 should share a fingerprint.
  const route = window.location.pathname
    .replace(
      /\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
      "/:id"
    )
    .replace(/\/\d+/g, "/:id");

  // Better fingerprint: type + stack hash + route
  const errorType = err.name || "Error";
  const effectiveStack = stackOverride ?? err.stack;
  const stackHash =
    effectiveStack && effectiveStack.length > 0
      ? hashStack(effectiveStack)
      : "no-stack";

  return `${errorType}_${stackHash}_${route}`;
};

/**
 * Check if we should trigger replay for this error (deduplication)
 * Returns replay decision + occurrence count
 */
export const shouldTriggerReplay = (
  fingerprint: string,
  currentUserAction?: string
): { shouldReplay: boolean; count: number } => {
  const cached = errorCache.get(fingerprint);
  const now = Date.now();
  const userActionKey = currentUserAction || "unknown";

  if (!cached) {
    // First occurrence
    errorCache.set(fingerprint, {
      lastSeen: now,
      count: 1,
      lastUserAction: userActionKey,
    });
    return { shouldReplay: true, count: 1 };
  }

  // Increment count
  cached.count += 1;

  // Trigger replay if:
  // 1. Not seen in last hour, OR
  // 2. User action changed (user tried different approach) AND at least 5 mins since last
  const timeSinceLast = now - cached.lastSeen;
  const userActionChanged = cached.lastUserAction !== userActionKey;

  if (
    timeSinceLast > 3600000 ||
    (userActionChanged && timeSinceLast > 300000)
  ) {
    cached.lastSeen = now;
    cached.lastUserAction = userActionKey;
    errorCache.set(fingerprint, cached);
    return { shouldReplay: true, count: cached.count };
  }

  // No replay, but return count
  return { shouldReplay: false, count: cached.count };
};

/**
 * Filter out third-party/benign errors
 * Returns true if error originated from app code
 */
export const isAppError = (error: Error | string, stack?: string): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  const errorStack = stack || (error instanceof Error ? error.stack : "") || "";

  // Exclude known noise patterns
  const noisePatterns = [
    "Script error",
    "ResizeObserver",
    "extension",
    "chrome-extension",
    "moz-extension",
    "safari-extension",
  ];

  if (
    noisePatterns.some(
      (pattern) => message.includes(pattern) || errorStack.includes(pattern)
    )
  ) {
    return false;
  }

  // Only app errors if we can verify origin
  // Skip this check if no stack available (some errors don't have stack)
  // Note: In some build environments (webpack eval, etc.), hostname may not appear in stack
  // Only filter if stack clearly indicates external origin (http:// or https:// with different domain)
  if (errorStack && errorStack.length > 0) {
    const hasExternalUrl = /https?:\/\/([^/]+)/.exec(errorStack);
    if (hasExternalUrl) {
      const stackDomain = hasExternalUrl[1];
      // Filter only if stack explicitly shows different domain
      if (
        !stackDomain.includes(window.location.hostname) &&
        !window.location.hostname.includes(stackDomain)
      ) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Check if error should trigger replay (combines filtering + deduplication)
 * Returns replay decision + occurrence count
 */
export const shouldRecordErrorReplay = (
  error: Error | string,
  stack?: string,
  userAction?: string
): { shouldReplay: boolean; occurrenceCount: number } => {
  // Filter third-party errors
  if (!isAppError(error, stack)) {
    return { shouldReplay: false, occurrenceCount: 0 };
  }

  // Check deduplication with user action context
  const fingerprint = getErrorFingerprint(error, stack);
  const result = shouldTriggerReplay(fingerprint, userAction);
  return { shouldReplay: result.shouldReplay, occurrenceCount: result.count };
};
